import { type Unit } from '@dynatrace-sdk/units';

import {
  CustomColorPalette,
  ColorPalette,
} from '../../core/types/color-palette.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { InternalSliceData } from '../types/internal/pie-chart.js';
import { SliceData } from '../types/pie-chart.config.js';
import { getSlicePercentage } from '../utils/plot-data.utils.js';

const DEFAULT_COLOR_PALETTE = 'categorical';

/**
 * Builds the slices and their configurations for the pie and donut chart.
 * @param data - Data received from the dataSet.
 * @param colorPalette - Color to apply to the slices.
 * @returns An array of internal slices.
 */
export const buildSlices = (
  data: SliceData[],
  colorPalette?: ColorPalette | CustomColorPalette,
  unit?: string | Unit,
): InternalSliceData[] => {
  const totalAbsoluteValueSum = Object.values(data).reduce(
    (total, { value }) => total + Math.abs(value),
    0,
  );

  return data.map((slice, index) => {
    const assignedPaletteColor = getColorPalette(
      slice.category,
      index,
      colorPalette ?? DEFAULT_COLOR_PALETTE,
    );
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const priorityColor = slice.color && { Default: slice.color };

    return {
      ...slice,
      id: `${slice.category}-${index}`,
      relativeValue: getSlicePercentage(slice.value, totalAbsoluteValueSum),
      color: priorityColor || assignedPaletteColor,
      _priorityColor: priorityColor || undefined,
      unit,
    };
  });
};
